
import API_URL from '../config';
// ── Session helpers ───────────────────────────────────────────
export const getSession = () => {
  try {
    const s = localStorage.getItem('biz_session');
    return s ? JSON.parse(s) : null;
  } catch { return null; }
};
export const setSession  = (d) => localStorage.setItem('biz_session', JSON.stringify(d));
export const clearSession = () => localStorage.removeItem('biz_session');


export const api = async (module, params = {}) => {
  const session = getSession();

  const body = {
    module,
    corp_id : session?.corp_id || 0,
    product : "quotationB" || 0,
    user_id : session?.id      || 0,
    ryts    : session?.ryts    || 2,
    ...params,
  };

  const res  = await fetch(API_URL, {
    method      : 'POST', 
    credentials : 'include',                        // sends cookies if any
    headers     : { 'Content-Type': 'application/json' },
    body        : JSON.stringify(body),
  });
  
  const text = await res.text();
  if (res.status === 401) {
    clearSession();
    throw new Error('Unauthorized: Please log in again.');
    }
  // Your PHP echoes "success: VALUE" or "Error: MSG" or raw JSON
  if (text.startsWith('success: ')) {
    const payload = text.slice(9).trim();
    try   { return JSON.parse(payload); }
    catch { return payload; }                       // plain string value
  }

  if (text.startsWith('Error:') || text.startsWith('Invalid') || text.startsWith('Invalid') || text.startsWith('error:')) {
    throw new Error(text.trim());
  }

  try   { return JSON.parse(text); }
  catch { return text; }
};


export const FollowUps = {
  markDone: (customer_id) => api('markFollowupDone', { customer_id }),
};

// ── AUTH ──────────────────────────────────────────────────────
// Your login returns: "success: id!@#$corp_id!@#$ryts!@#$name!@#$email!@#$business"
export const Auth = {
  login: async ({ corp_id, email, password }) => {
    const product = "quotationB";
    const raw = await api('login', { business: corp_id, email, password, product });

    // Parse your existing implode("!@#$", ...) format
    if (typeof raw === 'string' && raw.includes('!@#$')) {
      const [id, cid, ryts, name, eml, business, plan] = raw.split('!@#$'); 
      const user = { id: +id, corp_id: +cid, ryts, name, email: eml, business, plan: plan || 'free' }; 
      console.log(user);
      setSession(user);
      return user;
    }
    if (typeof raw === 'object' && raw?.id) {
      setSession(raw);
      return raw;
    }
    throw new Error(raw || 'Login failed');
  },

  signup: (d) => api('signup', d),

  logout: () => {
    clearSession();
    return Promise.resolve();
  },
};
// ── CUSTOMERS ─────────────────────────────────────────────────
// getCustomer returns { data: [{id , company, name, user_id, number, lstatus}] }
// customerM   returns insert_id (new) or json (update)
export const Customers = {
  list   : (cusID = 0) => api('getCustomer', { cusID }).then(r => r?.data || []),
  detail : (cusID)     => api('getCustomer', { cusID }).then(r => r?.data?.[0] || null),
  save   : (data)      => api('customerM',   data),
};

// ── PRODUCTS ──────────────────────────────────────────────────
// grabItems  returns { data: [{id, product_name, manufacture, sku, min_price, max_price, tax}] }
// updateItem updates min/max price (admin only)
// addItem    adds new product
export const Products = {
  list  : ()     => api('grabItems',  {}).then(r => r?.data || []),
  add   : (data) => api('addItem',    data),
  update: (data) => api('updateItem', data),
};

// ── TEAM / ORG MEMBERS (for dropdowns like Task assignment) ───
// Uses getOrgMem — separate from admin-only Users management,
// which is a DIFFERENT endpoint (getUsers) with a different shape.
export const TeamMembers = {
  list: (task = 'y') => api('getOrgMem', { task }).then(r => r?.data || []),
};

// ── LEADS ─────────────────────────────────────────────────────
// getLeads       returns { data: [{id, company, name, user_id, number, lstatus, lstat_dt}] }
// udtlStat       updates lead status + inserts lead_history
// getStatHistory returns { data: [{status_id, date, reason, user_id}] }
// filterHistory  returns quote history for a customer (optionally filtered by product)
export const Leads = {
  list      : (cusID = 0) => api('getLeads',       { cusID }).then(r => r?.data || []),
  detail    : (cusID)     => api('getLeads',       { cusID }).then(r => r?.data?.[0] || null),
  updateStat: (data)      => api('udtlStat',        data),
  history   : (cusID)     => api('getStatHistory', { cusID }).then(r => r?.data || []),
  report    : ()          => api('getLeadReport',  {}).then(r => r?.data || []),
  sources   : ()          => api('getSource',      {}).then(r => r?.data || []),
  statuses  : ()          => api('getStatus',      {}).then(r => r?.data || []),
  types     : ()          => api('getCType',       {}).then(r => r?.data || []),
  pastQuotes: (data)      => api('filterHistory',   data).then(r => r?.data || []),
};

// ── TASKS ─────────────────────────────────────────────────────
// getTsk    returns { data: [{id, title, status, fromusr, tousr, startdt, deadline, detail}] }
// getTskDtl returns single task + { indetail: [{detail, date, user_id}] }
// tasManage update status + add reply  OR  create new task
export const Tasks = {
  list  : ()     => api('getTsk',    {}).then(r => r?.data || []),
  detail: (idis) => api('getTskDtl', { idis }).then(r => r?.data || null),
  create: (data) => api('tasManage', {data, user_id}),   // no id = insert
  update: (data) => api('tasManage', data),   // has id = update status + reply
};

// ── EXPENSES ──────────────────────────────────────────────────
// getExp    returns { data: [{id, depart, arrive, kms, amt, notes, dtime, user_id, ac_id, ac_dt, fin_id, fin_date}] }
// addExp    inserts expense
// accAction approves (who='exp' manager, who='exp_fin' finance)
//   FIXED: matches the accAction() 'who' values fixed in quotationB.php
//   (rejection uses the SAME "who" bucket as approval — the PHP branch
//   decides ac_id vs fin_id column, so reject just needs a status flag;
//   see quoteAction/accAction PHP notes below for the reject convention).
export const Expenses = {
  list   : ()        => api('getExp',    {}).then(r => r?.data || []),
  submit : (data)    => api('addExp',    data),
  approveManager: (id, user_id) => api('accAction', { id, who: 'exp', user_id }),
  rejectManager:  (id, user_id, reason) => api('accAction', { id, who: 'exp_reject', user_id, reason }),
  approveFinance: (id, user_id) => api('accAction', { id, who: 'exp_fin', user_id }),
  rejectFinance:  (id, user_id, reason) => api('accAction', { id, who: 'exp_fin_reject', user_id, reason }),
};

// ── CALLS (NEW — needs quotationB_extension.php) ──────────────
// ⚠️  Notify backend: add logCall / getCalls / updateCallOutcome to quotationB.php
export const Calls = {
  list         : ()     => api('getCalls',          {}).then(r => r?.data || []),
  log          : (data) => api('logCall',            data),
  updateOutcome: (data) => api('updateCallOutcome',  data),
};

// ── OCR / CARD SCANNER (NEW — needs quotationB_extension.php) ─
// ⚠️  Notify backend: add scanCard / saveCardAsCustomer / getCardHistory
export const OCR = {
  scan          : (image_base64) => api('scanCard',           { image_base64 }),
  saveAsCustomer: (data)         => api('saveCardAsCustomer',  data),
  history       : ()             => api('getCardHistory',      {}).then(r => r?.data || []),
};

// ── REPORTS ───────────────────────────────────────────────────
// FIXED: was calling a non-existent module "getRepots" (typo) via
// Quotes.report(), which also silently dropped the from/to date range.
// Now calls the real PHP function forReport() with the actual params.
export const Reports = {
  sales: (from, to) => api('forReport', { from, to }).then(r => r?.data || []),
  leads: () => Leads.report(),
  byLocation: (from, to) => api('getLocationReport', { from, to }),
  dailyStatus: (date) => api('getDailyQuoteStatusReport', { date }),
  quotesByStatus: (date, qstatus) => api('getQuotesByStatusDate', { date, qstatus }).then(r => r?.data || []),
};

// ── VISITS (NEW — needs quotationB_extension.php) ─────────────
// ⚠️  Notify backend: add addVisit / getVisits / approveVisit
export const Visits = {
  list   : ()     => api('getVisits',    {}).then(r => r?.data || []),
  add    : (data) => api('addVisit',      data),
  approve: (data) => api('approveVisit',  data),
};

// ── PDF ──────────────────────────────────────────────────────
export const PDF = {
    /**
     * Get a short-lived signed URL to download the PDF.
     * Opens it in a new tab — works on mobile too.
     */
    download: async (quote_id, corp_id) => {
        const data = await api('getPDFToken', { quote_id, corp_id });
        if (data.url) {
            window.open("Pdf/" + data.url, '_blank');
        } else {
            throw new Error(data.error || 'Could not generate PDF');
        }
    },
};

// ── Users (admin-only user management — NOT the same as TeamMembers) ──
export const Users = {
    /** List all users in the corp — admin only */
    list: () => api('getUsers', {}),

    /** Create (id=0) or update (id=N) a user */
    save: (userData) => api('manageUser', userData),

    /** Toggle active/inactive — pass { target_id, active: 0|1 } */
    setActive: (target_id, active) => api('deactivateUser', { target_id, active }),

    /** Admin reset another user's password */
    resetPassword: (target_id, password) => api('resetUserPassword', { target_id, password }),
};

// ── Company Settings (manager-only) ────────────────────────────
export const Company = {
  get: () => api('getCompanySettings', {}).then(r => r?.data?.[0] || null),
  update: (data) => api('updateCompany', data),

  banks: () => api('getCompanySettings', {}).then(r => Array.isArray(r?.data?.[0]?.bank?.data) ? r.data[0].bank.data : []),
  saveBank: (data) => api('saveBank', data),
  deleteBank: (id) => api('deleteBank', { id }),

  addresses: () => api('getCompanySettings', {}).then(r => Array.isArray(r?.data?.[0]?.address?.data) ? r.data[0].address.data : []),
  saveAddress: (data) => api('saveAddress', data),
  deleteAddress: (id) => api('deleteAddress', { id }),
};

// ── QUOTES ────────────────────────────────────────────────────
// FIXED: removed duplicate `statuses`/`updateStatus` keys (silently
// shadowed by later definitions before — confusing to maintain).
// FIXED: approveManager/rejectManager/approveFinance/rejectFinance
// now call a dedicated `quoteAction` PHP module instead of piggybacking
// on `quoteM` (which is the general save/update quote function and has
// no idea what an `action` param means — it would have corrupted quotes).
// See the quoteAction() PHP function provided separately.
// REMOVED: `Quotes.setStatus` was referenced by QuotesList.jsx's dead
// convertToInvoice() but never defined anywhere — that function has
// been removed from QuotesList.jsx since the new approval flow replaces it.
export const Quotes = {
    list        : ()        => api('getQuote',    { id:0 }).then(r => r.data || []),
    detail      : (id)      => api('getQuote',    { id   }).then(r => r.data?.[0] ?? null),
    invoices    : ()        => api('getQuoteAcc', { id:0 }).then(r => r.data || []),
    save        : (payload) => api('quoteM',       payload),

    statuses    : ()        => api('getQuoteStatuses', {}).then(r => r.data || []),
    updateStatus: (payload) => api('manageQStatus', payload),

    // Multi-level approval — Manager then Finance
    approveManager: (id, user_id) => api('accAction', { id, action: 'accAction', who:'inv', user_id }),
    rejectManager:  (id, user_id, reason) => api('accAction', { id, action: 'reject_manager', user_id, reason }),
    approveFinance: (id, user_id) => api('accAction', { id, action: 'approve_finance', who:'inv', user_id }),
    rejectFinance:  (id, user_id, reason) => api('accAction', { id, action: 'reject_finance', user_id, reason }),
};

export const QuotesFull = {
    list        : ()        => api('getQuote',    { id:0 }).then(r => r.data || []),
    detail      : (id)      => api('getQuote',    { id   }).then(r => r.data?.[0] ?? null),
    invoices    : ()        => api('getQuoteAcc', { id:0 }).then(r => r.data || []),
    save        : (payload) => api('quoteM',       payload),
    statuses    : ()        => api('getQuoteStatuses', {}).then(r => r.data || []),
    updateStatus: (payload) => api('manageQStatus', payload),
};

export const Plan = {
  usage: () => api('getPlanUsage', {}),
  // Admin/internal-tool only — see PHP-side auth note. Not meant to
  // be called by a self-serve "upgrade" button without real billing
  // behind it.
  setPlan: (plan) => api('setCorpPlan', { plan }),
};

export const Billing = {
  createOrder: (plan) => api('createRazorpayOrder', { plan }),
  verifyPayment: (payload) => api('verifyRazorpayPayment', payload),
};

export const LeadIntake = {
    /** List inbound leads with optional filters */
    list: (filters = {}) =>
        api('getInboundLeads', filters).then(r => r.data || []),

    /** Single lead with raw payload */
    detail: (lead_id) =>
        api('getInboundLead', { lead_id }).then(r => r.data || null),

    /** Convert inbound lead to customer (unassigned) */
    convert: (lead_id, assign_to = 0) =>
        api('convertLead', { lead_id, assign_to }),

    /** Mark as junk or duplicate */
    markJunk: (lead_id, mark_as = 'junk') =>
        api('markLeadJunk', { lead_id, mark_as }),

    /** Get lead sources with stats + webhook URLs */
    sources: () =>
        api('getLeadSources', {}).then(r => r.data || []),

    /** Regenerate webhook token for a source */
    regenerateToken: (source_id) =>
        api('regenerateToken', { source_id }),

    /** Stats for all sources */
    stats: (days = 30) =>
        api('getLeadIntakeStats', { days }).then(r => r),

    /** CSV upload — uses FormData, not JSON */
    importCSV: async (file, source_id, user) => {
        const form = new FormData();
        form.append('csv_file',  file);
        form.append('module',    'importLeadsCSV');
        form.append('source_id', source_id);
        form.append('corp_id',   user.corp_id);
        form.append('user_id',   user.id);

        // Use your base API URL here
        const BASE = '/api.php'; // adjust if different
        const res  = await fetch(BASE, { method: 'POST', body: form });
        return res.json();
    },
};
